// Add Obligation Wizard — the 6-step replacement for the Add Fee form
// (What is it → Who's involved → How it collects → Amount & date → Revenue
// pool → Review & create). Same modal-wizard mechanics as the Record Payment
// wizard: StatusStepper chips, per-step consequence copy, required
// acknowledgments before a risky Next, and a per-step "Ask AI" coach.
//
// The wizard writes a LoanFeeEntry through the UNCHANGED create endpoint —
// a borrower-payable entry carries the chosen WF1 obligation TypeCode and
// lands pending_review (B3 approval creates the linked obligation); any
// other payer stays a record-only tracker row. The curated type catalog +
// live ladder come from the server (useObligationCatalog) — the wizard never
// touches the waterfall itself.

import { useMemo, useState } from "react";
import { MessageCircleQuestion } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { Button, Input, Label, Textarea } from "@/design-system/components/ui";
import { StatusStepper } from "@/design-system/components/ui/StatusStepper";
import { Modal } from "@/components/ui/Modal";
import { formatCurrency } from "@/design-system/lib/format";
import { calendarDateInputToIso, isoDateInTz, useDateFormatter } from "@/utils/datetime";
import { commissionsApi } from "@/api/commissions";
import {
  PAYMENT_CHANNELS,
  type AddableObligationTypeDto,
  type LoanFeeDto,
} from "@/api/portfolioAdmin/loanFeesApi";
import {
  useCreateLoanFee,
  useLoanFees,
  useObligationCatalog,
} from "@/hooks/portfolioAdmin/useLoanFees";
import { loanFeesKeys } from "@/hooks/portfolioAdmin/loanFeesKeys";
import {
  CounterpartyPicker,
  type SelectedCounterparty,
} from "@/components/portfolioAdmin/loanDetail/parties/CounterpartyPicker";
import { ObligationCreationExplainModal } from "./ObligationCreationExplainModal";
import { ProjectedImpactPanel } from "./ProjectedImpactPanel";
import {
  ADD_OBLIGATION_STEPS,
  CUSTOM_LABEL_HELP,
  CUSTOM_OPTION_BLURB,
  CUSTOM_OPTION_TITLE,
  DORMANT_NOTE,
  DUPLICATE_ACK_LABEL,
  FIREABLE_NOTE,
  FUTURE_DATE_ERROR,
  LARGE_AMOUNT_WARNING,
  LIFECYCLE_OPTIONS,
  NON_BORROWER_ACK_LABEL,
  NON_BORROWER_WARNING,
  PARTIES_STEP_INTRO,
  PAYER_QUICK_OPTIONS,
  PIPELINE_SENTENCE,
  RECORD_ONLY_SENTENCE,
  RECORD_ONLY_SLOT_NOTE,
  REVIEW_BENEFICIARY_RECORD_ONLY,
  REVIEW_NOT_APPLICABLE,
  TYPE_STEP_INTRO,
  collectionStepIntro,
  duplicateWarning,
  friendlyServerError,
  poolDepartureNote,
  poolStepIntro,
  recipientSentence,
  type LifecycleValue,
} from "./addObligationCopy";

const SELECT_CLASS =
  "w-full border border-border-light rounded-md px-space-2 py-space-1 text-small bg-white-pure";

/** Fee statuses that still bill (or already billed) — what the duplicate
 *  check counts as "open" for the same target slot. */
const OPEN_FEE_STATUSES = new Set(["pending", "pending_review", "approved", "billed"]);

const LARGE_AMOUNT_THRESHOLD = 10_000;

const PARTY_TYPES = ["Person", "LegalEntity", "Business"] as const;
type PartyType = (typeof PARTY_TYPES)[number];

const CHANNEL_LABEL: Record<number, string> = Object.fromEntries(
  PAYMENT_CHANNELS.map((c) => [c.value, c.label]),
);

/** Count open fee entries already targeting the same slot (or carrying the
 *  same display label) — the entry-time duplicate check. Exported for tests. */
export function countOpenDuplicates(
  fees: LoanFeeDto[],
  effectiveTypeCode: string | null,
  displayLabel: string,
): number {
  if (!effectiveTypeCode && !displayLabel) return 0;
  return fees.filter(
    (f) =>
      OPEN_FEE_STATUSES.has(f.status) &&
      ((effectiveTypeCode != null && f.targetObligationTypeCode === effectiveTypeCode) ||
        (displayLabel.length > 0 &&
          f.feeType.trim().toLowerCase() === displayLabel.trim().toLowerCase())),
  ).length;
}

export function AddObligationWizard({
  loanId,
  open,
  onClose,
}: {
  loanId: string;
  open: boolean;
  onClose: () => void;
}) {
  const { tz } = useDateFormatter();
  const create = useCreateLoanFee(loanId);
  const catalogQuery = useObligationCatalog(loanId, open);
  const feesQuery = useLoanFees(loanId);

  // Optional pool options — graceful when the caller lacks Claims.Read (the
  // fee-entry precedent: 403/empty just means "no pool tag").
  const poolsQuery = useQuery({
    queryKey: loanFeesKeys.poolOptions(),
    queryFn: () => commissionsApi.listPools(true),
    enabled: open,
    retry: false,
  });
  const pools = useMemo(() => poolsQuery.data ?? [], [poolsQuery.data]);

  const [step, setStep] = useState(0);
  // "custom" is the custom/other card; a type code selects a catalog entry.
  const [selection, setSelection] = useState<string | null>(null);
  const [customLabel, setCustomLabel] = useState("");
  const [confirmAck, setConfirmAck] = useState(false);
  const [payer, setPayer] = useState("Borrower");
  const [nonBorrowerAck, setNonBorrowerAck] = useState(false);
  const [payerEntityType, setPayerEntityType] = useState<PartyType>("Person");
  const [payerEntity, setPayerEntity] = useState<SelectedCounterparty | null>(null);
  const [beneficiaryEntityType, setBeneficiaryEntityType] = useState<PartyType>("LegalEntity");
  const [beneficiaryEntity, setBeneficiaryEntity] = useState<SelectedCounterparty | null>(null);
  const [lifecycle, setLifecycle] = useState<LifecycleValue>("one_time");
  const [lifecycleDetail, setLifecycleDetail] = useState("");
  const [amount, setAmount] = useState("");
  // Seeded ONCE, in the viewer's zone. A lazy initialiser is deliberate: it
  // must not re-seed when `tz` resolves later, because by then the staff member
  // may have typed a date and a re-seed would overwrite it.
  const [incurredDate, setIncurredDate] = useState<string>(() => isoDateInTz(new Date(), tz));
  const [duplicateAck, setDuplicateAck] = useState(false);
  const [revenuePoolId, setRevenuePoolId] = useState<string>("");
  const [poolTouched, setPoolTouched] = useState(false);
  const [notes, setNotes] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [created, setCreated] = useState<LoanFeeDto | null>(null);
  const [askAiOpen, setAskAiOpen] = useState(false);

  const catalog = catalogQuery.data;
  const fees = useMemo(() => feesQuery.data?.fees ?? [], [feesQuery.data]);

  const isCustom = selection === "custom";
  const template: AddableObligationTypeDto | null = useMemo(
    () =>
      !selection || isCustom
        ? null
        : (catalog?.types.find((t) => t.typeCode === selection) ?? null),
    [catalog, selection, isCustom],
  );

  // Custom entries route to the catalog's fallback slot server-side — the
  // consequence visual highlights that rung.
  const effectiveTypeCode = isCustom
    ? (catalog?.customFallbackTypeCode ?? null)
    : (selection ?? null);
  const displayLabel = isCustom ? customLabel.trim() : (template?.displayName ?? "");

  const highlightedRung = useMemo(
    () =>
      effectiveTypeCode
        ? (catalog?.ladder.find((r) => r.typeCode === effectiveTypeCode) ?? null)
        : null,
    [catalog, effectiveTypeCode],
  );

  // Smart defaults (AF7): selecting a type seeds channel/pool/lifecycle from
  // its template; the custom card seeds the plain Phoenix-fee defaults.
  const defaultPoolId = useMemo(() => {
    const code = isCustom ? null : (template?.defaultPoolCode ?? null);
    if (!code) return "";
    return pools.find((p) => p.code === code)?.id ?? "";
  }, [isCustom, template, pools]);

  const selectType = (value: string) => {
    setSelection(value);
    setConfirmAck(false);
    setDuplicateAck(false);
    setPoolTouched(false);
    const t = value === "custom" ? null : catalog?.types.find((x) => x.typeCode === value);
    setLifecycle((t?.defaultLifecycle as LifecycleValue) ?? "one_time");
  };

  // Effective pool: the template default until the user touches the select.
  const effectivePoolId = poolTouched ? revenuePoolId : defaultPoolId;
  const effectivePool = pools.find((p) => p.id === effectivePoolId) ?? null;
  const defaultPool = pools.find((p) => p.id === defaultPoolId) ?? null;
  const departedFromDefaultPool =
    poolTouched && defaultPoolId !== "" && effectivePoolId !== defaultPoolId;

  const isBorrower = payer.trim().toLowerCase() === "borrower";

  const parsedAmount = Number(amount);
  const amountValid = Number.isFinite(parsedAmount) && parsedAmount > 0;
  const largeAmount = amountValid && parsedAmount >= LARGE_AMOUNT_THRESHOLD;

  // Today in the VIEWER's zone, not Phoenix. This gates `dateIsFuture`, and
  // it is the ONLY bound on the incurred date — `LoanFeeService` has no
  // server-side future check — so a Phoenix-pinned "today" does not mislabel
  // anything, it REFUSES valid input: after 17:00 Phoenix a viewer east of it
  // is on tomorrow's date and could not record a cost incurred today.
  const todayStr = isoDateInTz(new Date(), tz);
  const dateIsFuture = incurredDate.length > 0 && incurredDate > todayStr;
  const dateValid = /^\d{4}-\d{2}-\d{2}$/.test(incurredDate) && !dateIsFuture;

  const duplicateCount = useMemo(
    () => countOpenDuplicates(fees, effectiveTypeCode, displayLabel),
    [fees, effectiveTypeCode, displayLabel],
  );

  const paymentChannel = template?.defaultPaymentChannel ?? 1;

  // --- Per-step gates --------------------------------------------------------
  const typeStepValid =
    selection != null &&
    (!isCustom ? true : customLabel.trim().length > 0) &&
    (template?.confirmBeforeAdding ? confirmAck : true);
  const partiesStepValid = payer.trim().length > 0 && (isBorrower || nonBorrowerAck);
  const amountStepValid =
    amountValid && dateValid && (duplicateCount === 0 || duplicateAck);

  const isLast = step === ADD_OBLIGATION_STEPS.length - 1;
  const nextDisabled =
    (step === 0 && !typeStepValid) ||
    (step === 1 && !partiesStepValid) ||
    (step === 3 && !amountStepValid) ||
    (isLast && create.isPending);

  const submit = async () => {
    setError(null);
    const incurredIso = calendarDateInputToIso(incurredDate);
    if (!incurredIso) {
      setError("Enter a valid incurred date.");
      return;
    }
    try {
      const fee = await create.mutateAsync({
        feeType: displayLabel,
        description: null,
        amount: parsedAmount,
        revenuePoolId: effectivePoolId || null,
        paymentChannel,
        payer: payer.trim(),
        incurredDate: incurredIso,
        notes: notes.trim() || null,
        // A record-only entry names NO obligation type: nothing collects, so
        // there is no waterfall slot to route to — and the server refuses a
        // type code on a non-borrower payer precisely because it would be a
        // claim about borrower billing that this row can never honour. Custom
        // entries omit it on both paths; on the borrower path the server routes
        // them to the catalog's fallback slot off customLabel and flags for
        // admin review.
        targetObligationTypeCode: isBorrower && !isCustom ? selection : null,
        lifecycleType: lifecycle,
        lifecycleDetail: lifecycleDetail.trim() || null,
        customLabel: isCustom ? customLabel.trim() : null,
        payerEntityId: payerEntity?.id ?? null,
        beneficiaryEntityId: beneficiaryEntity?.id ?? null,
      });
      setCreated(fee);
    } catch (err) {
      setError(
        friendlyServerError(err instanceof Error ? err.message : ""),
      );
    }
  };

  const handleNext = () => {
    if (isLast) {
      void submit();
      return;
    }
    setStep((s) => Math.min(s + 1, ADD_OBLIGATION_STEPS.length - 1));
  };
  const handleBack = () => setStep((s) => Math.max(s - 1, 0));

  return (
    <Modal
      open={open}
      onClose={onClose}
      dismissOnBackdropClick={false}
      size="lg"
      title="Add fee / obligation"
      description="Records a loan cost. A borrower-payable entry goes to B3 review, bills on the next statement, and collects through the waterfall."
      footer={
        created ? (
          <Button onClick={onClose} data-testid="wizard-done">
            Done
          </Button>
        ) : (
          <>
            <Button
              variant="secondary"
              onClick={onClose}
              disabled={create.isPending}
              data-testid="wizard-cancel"
            >
              Cancel
            </Button>
            {step > 0 && (
              <Button
                variant="secondary"
                onClick={handleBack}
                disabled={create.isPending}
                data-testid="wizard-back"
              >
                Back
              </Button>
            )}
            <Button
              loading={create.isPending}
              disabled={nextDisabled}
              onClick={handleNext}
              data-testid="wizard-next"
            >
              {isLast ? "Create" : "Next"}
            </Button>
          </>
        )
      }
    >
      <div className="flex flex-col gap-space-4" data-testid="add-obligation-wizard">
        {!created && (
          <div className="flex items-start justify-between gap-space-3">
            <StatusStepper
              steps={ADD_OBLIGATION_STEPS.map((s) => ({ key: s.key, label: s.label }))}
              currentIndex={step}
              label="Add obligation steps"
              data-testid="add-obligation-stepper"
            />
            <Button
              type="button"
              variant="text"
              size="sm"
              onClick={() => setAskAiOpen(true)}
              data-testid="ask-ai-button"
            >
              <MessageCircleQuestion size={14} aria-hidden /> Ask AI
            </Button>
          </div>
        )}

        {created ? (
          <div className="flex flex-col gap-space-3" data-testid="create-success">
            <p className="text-small text-onyx m-0">
              {created.sourceType
                ? `${created.feeType} (${formatCurrency(created.amount)}) sent to B3 fee review.`
                : `${created.feeType} (${formatCurrency(created.amount)}) recorded for tracking.`}
            </p>
            <p className="text-caption text-gray-mid m-0">
              {created.sourceType
                ? "Once approved it becomes a waterfall obligation and bills on the borrower's next statement. Track it in the review section above."
                : "Record-only entries never bill or collect — manage their status from the fees table."}
            </p>
          </div>
        ) : (
          <>
            {step === 0 && (
              <div className="flex flex-col gap-space-3" data-testid="step-type">
                <p className="text-small text-gray-mid m-0">{TYPE_STEP_INTRO}</p>
                {catalogQuery.isLoading ? (
                  <p className="text-small text-gray-mid m-0">Loading the catalog…</p>
                ) : catalogQuery.isError || !catalog ? (
                  <p className="text-small text-status-error m-0" role="alert">
                    The obligation catalog could not be loaded. Close and try again.
                  </p>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-space-2">
                    {catalog.types.map((t) => (
                      <TypeCard
                        key={t.typeCode}
                        title={t.displayName}
                        blurb={t.description ?? ""}
                        selected={selection === t.typeCode}
                        onClick={() => selectType(t.typeCode)}
                        testId={`type-option-${t.typeCode}`}
                      />
                    ))}
                    <TypeCard
                      title={CUSTOM_OPTION_TITLE}
                      blurb={CUSTOM_OPTION_BLURB}
                      selected={isCustom}
                      onClick={() => selectType("custom")}
                      testId="type-option-custom"
                    />
                  </div>
                )}

                {isCustom && (
                  <div>
                    <Label htmlFor="custom-label">Fee name</Label>
                    <Input
                      id="custom-label"
                      autoFocus
                      value={customLabel}
                      onChange={(e) => setCustomLabel(e.target.value)}
                      placeholder="e.g. Site visit cost"
                      data-testid="custom-label"
                    />
                    <p className="text-caption text-gray-mid m-0 mt-space-1">
                      {CUSTOM_LABEL_HELP}
                    </p>
                  </div>
                )}

                {template?.confirmBeforeAdding && (
                  <div
                    className="flex flex-col gap-space-2 border border-status-warning rounded-md bg-status-warning/10 px-space-3 py-space-2"
                    data-testid="type-confirm"
                  >
                    <p className="text-small text-onyx m-0">
                      {template.displayName} is normally assessed automatically by
                      the system when the triggering event happens. Add it manually
                      only when you're sure no automatic assessment exists for it.
                    </p>
                    <label className="flex items-start gap-space-2 text-small text-onyx">
                      <input
                        type="checkbox"
                        checked={confirmAck}
                        onChange={(e) => setConfirmAck(e.target.checked)}
                        data-testid="type-confirm-checkbox"
                      />
                      No automatic assessment covers this — add it manually.
                    </label>
                  </div>
                )}
              </div>
            )}

            {step === 1 && (
              <div className="flex flex-col gap-space-3" data-testid="step-parties">
                <p className="text-small text-gray-mid m-0">{PARTIES_STEP_INTRO}</p>
                <div>
                  <Label htmlFor="payer-name">Payer</Label>
                  <div className="flex items-center gap-space-2 mb-space-1">
                    {PAYER_QUICK_OPTIONS.map((p) => (
                      <button
                        key={p}
                        type="button"
                        onClick={() => {
                          setPayer(p);
                          setNonBorrowerAck(false);
                        }}
                        aria-pressed={payer === p}
                        className={
                          "text-caption px-space-2 py-space-1 rounded-md border transition-colors duration-fast " +
                          (payer === p
                            ? "border-gold bg-gold/10 text-onyx"
                            : "border-border-light bg-white-pure text-gray-mid hover:border-gold")
                        }
                        data-testid={`payer-chip-${p}`}
                      >
                        {p}
                      </button>
                    ))}
                  </div>
                  <Input
                    id="payer-name"
                    value={payer}
                    onChange={(e) => {
                      setPayer(e.target.value);
                      setNonBorrowerAck(false);
                    }}
                    data-testid="payer-name"
                  />
                </div>

                {!isBorrower && payer.trim().length > 0 && (
                  <div
                    className="flex flex-col gap-space-2 border border-status-warning rounded-md bg-status-warning/10 px-space-3 py-space-2"
                    data-testid="non-borrower-warning"
                  >
                    <p className="text-small text-onyx m-0">{NON_BORROWER_WARNING}</p>
                    <label className="flex items-start gap-space-2 text-small text-onyx">
                      <input
                        type="checkbox"
                        checked={nonBorrowerAck}
                        onChange={(e) => setNonBorrowerAck(e.target.checked)}
                        data-testid="non-borrower-ack-checkbox"
                      />
                      {NON_BORROWER_ACK_LABEL}
                    </label>
                  </div>
                )}

                <p
                  className="text-small text-onyx m-0 p-space-3 border border-border-light rounded-md bg-white-warm"
                  data-testid="recipient-fact"
                >
                  {recipientSentence(template?.recipientDescription ?? null, isBorrower)}
                </p>

                <PartyLinkPicker
                  legend="Link the payer's record (optional)"
                  type={payerEntityType}
                  onTypeChange={(t) => {
                    setPayerEntityType(t);
                    setPayerEntity(null);
                  }}
                  value={payerEntity}
                  onChange={setPayerEntity}
                  testId="payer-entity"
                />
                <PartyLinkPicker
                  legend="Link the beneficiary's record (optional)"
                  type={beneficiaryEntityType}
                  onTypeChange={(t) => {
                    setBeneficiaryEntityType(t);
                    setBeneficiaryEntity(null);
                  }}
                  value={beneficiaryEntity}
                  onChange={setBeneficiaryEntity}
                  testId="beneficiary-entity"
                />
              </div>
            )}

            {step === 2 && (
              <div className="flex flex-col gap-space-3" data-testid="step-collection">
                <p className="text-small text-gray-mid m-0">
                  {collectionStepIntro(isBorrower)}
                </p>

                {catalog && (
                  <ol
                    className="m-0 p-0 list-none flex flex-col gap-px max-h-56 overflow-y-auto border border-border-light rounded-md"
                    data-testid="waterfall-ladder"
                  >
                    {catalog.ladder.map((r) => {
                      const highlighted = highlightedRung?.position === r.position;
                      return (
                        <li
                          key={r.position}
                          className={
                            "flex items-center gap-space-2 px-space-3 py-space-1 text-small " +
                            (highlighted
                              ? "bg-gold/10 border-l-2 border-gold font-semibold text-onyx"
                              : "text-gray-mid")
                          }
                          data-testid={highlighted ? "ladder-rung-highlighted" : undefined}
                        >
                          <span className="tabular-nums w-8 shrink-0">P{r.position}</span>
                          <span className="flex-1">{r.label}</span>
                          {r.isTakeAllRemaining && (
                            <span className="text-caption uppercase tracking-label">
                              takes remainder
                            </span>
                          )}
                        </li>
                      );
                    })}
                  </ol>
                )}

                {!isBorrower && (
                  <p
                    className="text-small text-onyx m-0 border border-border-light rounded-md bg-white-warm px-space-3 py-space-2"
                    data-testid="record-only-slot-note"
                  >
                    {RECORD_ONLY_SLOT_NOTE}
                  </p>
                )}
                {isBorrower && template && (
                  <p
                    className={
                      "text-small m-0 border rounded-md px-space-3 py-space-2 " +
                      (template.isCurrentlyFireable
                        ? "text-onyx border-border-light bg-white-warm"
                        : "text-status-warning border-status-warning bg-status-warning/10")
                    }
                    role={template.isCurrentlyFireable ? undefined : "status"}
                    data-testid={template.isCurrentlyFireable ? "fireable-note" : "dormant-note"}
                  >
                    {template.isCurrentlyFireable ? FIREABLE_NOTE : DORMANT_NOTE}
                  </p>
                )}
                {isBorrower && isCustom && (
                  <p
                    className="text-small text-onyx m-0 border border-border-light rounded-md bg-white-warm px-space-3 py-space-2"
                    data-testid="fireable-note"
                  >
                    {FIREABLE_NOTE}
                  </p>
                )}

                <div className="flex flex-col gap-space-2">
                  <Label>Lifecycle</Label>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-space-2">
                    {LIFECYCLE_OPTIONS.map((o) => (
                      <TypeCard
                        key={o.value}
                        title={o.label}
                        blurb={o.blurb}
                        selected={lifecycle === o.value}
                        onClick={() => setLifecycle(o.value)}
                        testId={`lifecycle-${o.value}`}
                      />
                    ))}
                  </div>
                  {lifecycle !== "one_time" && (
                    <div>
                      <Label htmlFor="lifecycle-detail">
                        {lifecycle === "event_triggered"
                          ? "What event triggers it?"
                          : "How often does it repeat?"}
                      </Label>
                      <Input
                        id="lifecycle-detail"
                        value={lifecycleDetail}
                        onChange={(e) => setLifecycleDetail(e.target.value)}
                        placeholder="Optional"
                        data-testid="lifecycle-detail"
                      />
                    </div>
                  )}
                </div>
              </div>
            )}

            {step === 3 && (
              <div className="flex flex-col gap-space-3" data-testid="step-amount">
                <div className="grid grid-cols-2 gap-space-3">
                  <div>
                    <Label htmlFor="obligation-amount">Amount</Label>
                    <Input
                      id="obligation-amount"
                      type="number"
                      step="0.01"
                      min="0.01"
                      required
                      autoFocus
                      value={amount}
                      onChange={(e) => setAmount(e.target.value)}
                      data-testid="obligation-amount"
                    />
                  </div>
                  <div>
                    <Label htmlFor="obligation-incurred">Incurred date</Label>
                    <Input
                      id="obligation-incurred"
                      type="date"
                      required
                      value={incurredDate}
                      onChange={(e) => setIncurredDate(e.target.value)}
                      data-testid="obligation-incurred"
                    />
                  </div>
                </div>

                {dateIsFuture && (
                  <p
                    className="text-small text-status-error m-0"
                    role="alert"
                    data-testid="date-future-error"
                  >
                    {FUTURE_DATE_ERROR}
                  </p>
                )}
                {largeAmount && (
                  <p
                    className="text-small text-status-warning m-0"
                    role="status"
                    data-testid="large-amount-warning"
                  >
                    {LARGE_AMOUNT_WARNING}
                  </p>
                )}

                {duplicateCount > 0 && (
                  <div
                    className="flex flex-col gap-space-2 border border-status-warning rounded-md bg-status-warning/10 px-space-3 py-space-2"
                    data-testid="duplicate-warning"
                  >
                    <p className="text-small text-onyx m-0">
                      {duplicateWarning(
                        duplicateCount,
                        displayLabel || "matching",
                        isBorrower,
                      )}
                    </p>
                    <label className="flex items-start gap-space-2 text-small text-onyx">
                      <input
                        type="checkbox"
                        checked={duplicateAck}
                        onChange={(e) => setDuplicateAck(e.target.checked)}
                        data-testid="duplicate-ack-checkbox"
                      />
                      {DUPLICATE_ACK_LABEL}
                    </label>
                  </div>
                )}
              </div>
            )}

            {step === 4 && (
              <div className="flex flex-col gap-space-3" data-testid="step-pool">
                <p className="text-small text-gray-mid m-0">{poolStepIntro(isBorrower)}</p>
                <div>
                  <Label htmlFor="obligation-pool">Revenue pool</Label>
                  <select
                    id="obligation-pool"
                    className={SELECT_CLASS}
                    value={effectivePoolId}
                    onChange={(e) => {
                      setPoolTouched(true);
                      setRevenuePoolId(e.target.value);
                    }}
                    data-testid="obligation-pool"
                  >
                    <option value="">None</option>
                    {pools.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.code} — {p.name}
                      </option>
                    ))}
                  </select>
                </div>
                {departedFromDefaultPool && defaultPool && (
                  <p
                    className="text-small text-status-warning m-0"
                    role="status"
                    data-testid="pool-departure-note"
                  >
                    {poolDepartureNote(`${defaultPool.code} — ${defaultPool.name}`)}
                  </p>
                )}
              </div>
            )}

            {step === 5 && (
              <div className="flex flex-col gap-space-3" data-testid="wizard-review">
                <dl className="grid grid-cols-[10rem_1fr] gap-y-space-2 text-small text-onyx m-0 p-space-3 border border-border-light rounded-md bg-white-warm">
                  <dt className="text-gray-mid">Type</dt>
                  <dd className="m-0 font-semibold" data-testid="review-type">
                    {displayLabel}
                    {isCustom && " (custom — flagged for catalog review)"}
                  </dd>
                  <dt className="text-gray-mid">Waterfall slot</dt>
                  <dd className="m-0" data-testid="review-slot">
                    {!isBorrower
                      ? REVIEW_NOT_APPLICABLE
                      : highlightedRung
                        ? `P${highlightedRung.position} — ${highlightedRung.label}`
                        : "—"}
                  </dd>
                  <dt className="text-gray-mid">Payer</dt>
                  <dd className="m-0">
                    {payer}
                    {payerEntity ? ` (${payerEntity.name})` : ""}
                  </dd>
                  <dt className="text-gray-mid">Beneficiary</dt>
                  <dd className="m-0">
                    {beneficiaryEntity?.name ??
                      (isBorrower
                        ? (template?.recipientDescription ?? "Resolved by the waterfall slot")
                        : REVIEW_BENEFICIARY_RECORD_ONLY)}
                  </dd>
                  <dt className="text-gray-mid">Lifecycle</dt>
                  <dd className="m-0">
                    {LIFECYCLE_OPTIONS.find((o) => o.value === lifecycle)?.label}
                    {lifecycleDetail.trim() ? ` — ${lifecycleDetail.trim()}` : ""}
                  </dd>
                  <dt className="text-gray-mid">Amount</dt>
                  <dd className="m-0" data-testid="review-amount">
                    {amountValid ? formatCurrency(parsedAmount) : "—"}
                  </dd>
                  <dt className="text-gray-mid">Incurred</dt>
                  <dd className="m-0">{incurredDate}</dd>
                  <dt className="text-gray-mid">Payment channel</dt>
                  <dd className="m-0" data-testid="review-channel">
                    {isBorrower
                      ? (CHANNEL_LABEL[paymentChannel] ?? `Channel ${paymentChannel}`)
                      : REVIEW_NOT_APPLICABLE}
                  </dd>
                  <dt className="text-gray-mid">Revenue pool</dt>
                  <dd className="m-0">
                    {effectivePool ? `${effectivePool.code} — ${effectivePool.name}` : "None"}
                  </dd>
                </dl>

                <p className="text-small text-onyx m-0" data-testid="pipeline-sentence">
                  {isBorrower ? PIPELINE_SENTENCE : RECORD_ONLY_SENTENCE}
                </p>

                {isBorrower && amountValid && (
                  <ProjectedImpactPanel
                    loanId={loanId}
                    amount={parsedAmount}
                    typeLabel={displayLabel || "obligation"}
                    priorityLabel={
                      highlightedRung
                        ? `P${highlightedRung.position} — ${highlightedRung.label}`
                        : null
                    }
                    lifecycleLabel={
                      LIFECYCLE_OPTIONS.find((o) => o.value === lifecycle)?.label ?? ""
                    }
                  />
                )}

                <div>
                  <Label htmlFor="obligation-notes">Notes</Label>
                  <Textarea
                    id="obligation-notes"
                    rows={2}
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Optional"
                    data-testid="obligation-notes"
                  />
                </div>

                {error && (
                  <p
                    className="text-small text-status-error m-0"
                    role="alert"
                    data-testid="create-error"
                  >
                    {error}
                  </p>
                )}
              </div>
            )}
          </>
        )}

        {askAiOpen && (
          <ObligationCreationExplainModal
            loanId={loanId}
            context={{
              typeCode: effectiveTypeCode,
              customLabel: isCustom ? customLabel.trim() || null : null,
              amount: amountValid ? parsedAmount : null,
              incurredDate: dateValid ? calendarDateInputToIso(incurredDate) : null,
              lifecycleType: lifecycle,
              lifecycleDetail: lifecycleDetail.trim() || null,
              poolCode: effectivePool?.code ?? null,
              payerName: payer.trim() || null,
              beneficiaryName: beneficiaryEntity?.name ?? null,
              stepKey: ADD_OBLIGATION_STEPS[step].key,
            }}
            onClose={() => setAskAiOpen(false)}
          />
        )}
      </div>
    </Modal>
  );
}

function TypeCard({
  title,
  blurb,
  selected,
  onClick,
  testId,
}: {
  title: string;
  blurb: string;
  selected: boolean;
  onClick: () => void;
  testId: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={selected}
      className={
        "text-left p-space-3 rounded-md border transition-colors duration-fast " +
        (selected
          ? "border-gold bg-gold/10"
          : "border-border-light bg-white-pure hover:border-gold")
      }
      data-testid={testId}
    >
      <div className="font-display text-label font-semibold uppercase tracking-label text-onyx">
        {title}
      </div>
      <div className="text-caption text-gray-mid mt-space-1">{blurb}</div>
    </button>
  );
}

function PartyLinkPicker({
  legend,
  type,
  onTypeChange,
  value,
  onChange,
  testId,
}: {
  legend: string;
  type: PartyType;
  onTypeChange: (t: PartyType) => void;
  value: SelectedCounterparty | null;
  onChange: (v: SelectedCounterparty | null) => void;
  testId: string;
}) {
  return (
    <fieldset className="m-0 p-space-3 border border-border-light rounded-md flex flex-col gap-space-2">
      <legend className="text-caption uppercase tracking-label text-gray-mid px-space-1">
        {legend}
      </legend>
      <div className="flex items-center gap-space-2">
        {PARTY_TYPES.map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => onTypeChange(t)}
            aria-pressed={type === t}
            className={
              "text-caption px-space-2 py-space-1 rounded-md border transition-colors duration-fast " +
              (type === t
                ? "border-gold bg-gold/10 text-onyx"
                : "border-border-light bg-white-pure text-gray-mid hover:border-gold")
            }
            data-testid={`${testId}-type-${t}`}
          >
            {t === "LegalEntity" ? "Entity" : t}
          </button>
        ))}
      </div>
      <div data-testid={testId}>
        <CounterpartyPicker counterpartyType={type} value={value} onChange={onChange} />
      </div>
    </fieldset>
  );
}
